import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Award, Clock, CheckCircle, XCircle, ChevronDown, ChevronUp,
  Users, Building2, User, AlertTriangle, TrendingUp, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const RecapitulatifAutonomies = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStagiaire, setExpandedStagiaire] = useState(null);
  const [filter, setFilter] = useState('all'); // all, en_attente, acceptees, refusees
  const [heuresData, setHeuresData] = useState({}); // { stagiaire_id: [...sessions] }

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await axios.get('/centre/recapitulatif-autonomies');
      setData(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadHeures = async (stagiaireId) => {
    if (heuresData[stagiaireId]) return; // Already loaded
    try {
      const response = await axios.get(`/evaluations-entreprise/stagiaire/${stagiaireId}`);
      setHeuresData(prev => ({ ...prev, [stagiaireId]: response.data }));
    } catch (error) {
    }
  };

  const toggleStagiaire = (stagiaireId) => {
    const newExpanded = expandedStagiaire === stagiaireId ? null : stagiaireId;
    setExpandedStagiaire(newExpanded);
    if (newExpanded) loadHeures(stagiaireId);
  };

  const getHeuresResume = (stagiaireId) => {
    const sessions = heuresData[stagiaireId] || [];
    const parCategorie = {};
    const parSousCategorie = {};
    sessions.forEach(s => {
      const cat = s.categorie || '';
      const sousCat = s.sous_categorie || '';
      if (!parCategorie[cat]) parCategorie[cat] = 0;
      parCategorie[cat] += s.heures_encadrement || 0;
      const key = `${cat}__${sousCat}`;
      if (!parSousCategorie[key]) parSousCategorie[key] = { categorie: cat, sous_categorie: sousCat, heures: 0 };
      parSousCategorie[key].heures += s.heures_encadrement || 0;
    });
    return { parCategorie, parSousCategorie: Object.values(parSousCategorie), totalSessions: sessions.length };
  };

  const filteredStagiaires = data?.stagiaires?.filter(stag => {
    if (filter === 'all') return true;
    if (filter === 'en_attente') return stag.stats.en_attente > 0;
    if (filter === 'acceptees') return stag.stats.acceptees > 0;
    if (filter === 'refusees') return stag.stats.refusees > 0;
    return true;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recapitulatif-autonomies">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Récapitulatif des Autonomies</h1>
        <p className="text-slate-600">Suivi en temps réel des validations d'autonomie par les stagiaires</p>
      </div>

      {/* Stats globales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500 rounded-lg">
              <Award className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{data?.stats_global?.total_double_validation || 0}</p>
              <p className="text-xs text-blue-600">Doubles validations</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500 rounded-lg">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-700">{data?.stats_global?.total_en_attente || 0}</p>
              <p className="text-xs text-orange-600">En attente</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500 rounded-lg">
              <CheckCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">{data?.stats_global?.total_acceptees || 0}</p>
              <p className="text-xs text-green-600">Acceptées</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500 rounded-lg">
              <XCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-700">{data?.stats_global?.total_refusees || 0}</p>
              <p className="text-xs text-red-600">Refusées</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-slate-500" />
        <span className="text-sm text-slate-600 mr-2">Filtrer:</span>
        {[
          { key: 'all', label: 'Tous', count: data?.stagiaires?.length || 0 },
          { key: 'en_attente', label: 'En attente', count: data?.stagiaires?.filter(s => s.stats.en_attente > 0).length || 0, color: 'orange' },
          { key: 'acceptees', label: 'Acceptées', count: data?.stagiaires?.filter(s => s.stats.acceptees > 0).length || 0, color: 'green' },
          { key: 'refusees', label: 'Refusées', count: data?.stagiaires?.filter(s => s.stats.refusees > 0).length || 0, color: 'red' }
        ].map(f => (
          <Button
            key={f.key}
            variant={filter === f.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.key)}
            className={filter === f.key ? 'bg-[#8DC63F] hover:bg-[#7db336]' : ''}
            data-testid={`filter-${f.key}`}
          >
            {f.label} ({f.count})
          </Button>
        ))}
      </div>

      {/* Liste des stagiaires */}
      <div className="space-y-3">
        {filteredStagiaires.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
            <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucun stagiaire ne correspond à ce filtre</p>
          </div>
        ) : (
          filteredStagiaires.map((stag) => (
            <div 
              key={stag.stagiaire_id}
              className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm"
              data-testid={`stagiaire-${stag.stagiaire_id}`}
            >
              {/* En-tête du stagiaire */}
              <div 
                className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => toggleStagiaire(stag.stagiaire_id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#8DC63F] to-[#6ba32e] rounded-full flex items-center justify-center text-white font-bold">
                      {stag.prenom?.[0]}{stag.nom?.[0]}
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0F172A]">{stag.prenom} {stag.nom}</h3>
                      <p className="text-sm text-slate-500">{stag.centre}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* Mini stats */}
                    <div className="hidden sm:flex items-center gap-3">
                      {stag.stats.en_attente > 0 && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                          <Clock className="h-3 w-3" />
                          {stag.stats.en_attente}
                        </span>
                      )}
                      {stag.stats.acceptees > 0 && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          <CheckCircle className="h-3 w-3" />
                          {stag.stats.acceptees}
                        </span>
                      )}
                      {stag.stats.refusees > 0 && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                          <XCircle className="h-3 w-3" />
                          {stag.stats.refusees}
                        </span>
                      )}
                    </div>
                    
                    <div className="text-slate-400">
                      {expandedStagiaire === stag.stagiaire_id ? (
                        <ChevronUp className="h-5 w-5" />
                      ) : (
                        <ChevronDown className="h-5 w-5" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Stats mobile */}
                <div className="flex sm:hidden items-center gap-2 mt-3">
                  <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                    <Award className="h-3 w-3" />
                    {stag.stats.double_validations} total
                  </span>
                  {stag.stats.en_attente > 0 && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                      <Clock className="h-3 w-3" />
                      {stag.stats.en_attente}
                    </span>
                  )}
                  {stag.stats.acceptees > 0 && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      <CheckCircle className="h-3 w-3" />
                      {stag.stats.acceptees}
                    </span>
                  )}
                  {stag.stats.refusees > 0 && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                      <XCircle className="h-3 w-3" />
                      {stag.stats.refusees}
                    </span>
                  )}
                </div>
              </div>

              {/* Détails des autonomies */}
              {expandedStagiaire === stag.stagiaire_id && (
                <div className="border-t border-slate-200 bg-slate-50 p-4 space-y-4">
                  {/* Heures d'encadrement cumulées */}
                  {(() => {
                    const resume = getHeuresResume(stag.stagiaire_id);
                    const hasSessions = resume.totalSessions > 0;
                    return (
                      <div data-testid={`heures-${stag.stagiaire_id}`}>
                        <h4 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                          <Clock className="h-4 w-4 text-[#8DC63F]" />
                          Heures d'encadrement cumulées
                          {hasSessions && (
                            <span className="text-xs font-normal text-slate-500">({resume.totalSessions} session(s))</span>
                          )}
                        </h4>
                        {!hasSessions ? (
                          <p className="text-sm text-slate-500 italic">Aucune session d'évaluation enregistrée</p>
                        ) : (
                          <div className="space-y-3">
                            {/* Totaux par catégorie */}
                            <div className="flex flex-wrap gap-3">
                              {Object.entries(resume.parCategorie).map(([cat, h]) => (
                                <div key={cat} className="px-4 py-2 bg-white border border-[#8DC63F] rounded-lg text-center">
                                  <p className="text-xl font-bold text-[#0F172A]">{h}h</p>
                                  <p className="text-xs text-slate-600">{cat}</p>
                                </div>
                              ))}
                            </div>
                            {/* Détail par sous-catégorie */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {resume.parSousCategorie.map((sc) => (
                                <div key={`${sc.categorie}__${sc.sous_categorie}`} className="flex items-center justify-between bg-white p-2 rounded border border-slate-200">
                                  <div>
                                    <span className="text-xs font-medium text-slate-700">{sc.sous_categorie}</span>
                                    <span className="text-xs text-slate-400 ml-1">({sc.categorie})</span>
                                  </div>
                                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 text-xs rounded-full font-semibold">
                                    {sc.heures}h
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Autonomies */}
                  {stag.autonomies.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-slate-700 mb-3">Détail des autonomies</h4>
                  <div className="space-y-2">
                    {stag.autonomies.map((auto, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg border relative ${
                          auto.stagiaire_accepte === true 
                            ? 'bg-green-50 border-green-300 shadow-sm' 
                            : auto.stagiaire_accepte === false 
                              ? 'bg-red-50 border-red-200' 
                              : 'bg-orange-50 border-orange-200'
                        }`}
                      >
                        {/* Pictogramme vert pour autonomie complètement validée */}
                        {auto.stagiaire_accepte === true && (
                          <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                            <CheckCircle className="h-4 w-4 text-white" />
                          </div>
                        )}
                        
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="flex items-center gap-3">
                            {/* Indicateur visuel de validation complète */}
                            {auto.stagiaire_accepte === true && (
                              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                                <Award className="h-5 w-5 text-white" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-[#0F172A]">{auto.categorie}</p>
                              <p className="text-sm text-slate-600">{auto.sous_categorie}</p>
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-2">
                            {/* Statut validations */}
                            <span className="flex items-center gap-1 px-2 py-1 bg-white text-green-700 text-xs rounded border border-green-200">
                              <User className="h-3 w-3" /> Tuteur ✓
                            </span>
                            <span className="flex items-center gap-1 px-2 py-1 bg-white text-green-700 text-xs rounded border border-green-200">
                              <Building2 className="h-3 w-3" /> Entreprise ✓
                            </span>
                            
                            {/* Réponse stagiaire */}
                            {auto.stagiaire_accepte === true && (
                              <span className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white text-xs rounded font-medium">
                                <CheckCircle className="h-3 w-3" /> Accepté
                              </span>
                            )}
                            {auto.stagiaire_accepte === false && (
                              <span className="flex items-center gap-1 px-2 py-1 bg-red-500 text-white text-xs rounded font-medium">
                                <XCircle className="h-3 w-3" /> Refusé
                              </span>
                            )}
                            {auto.stagiaire_accepte === null && (
                              <span className="flex items-center gap-1 px-2 py-1 bg-orange-500 text-white text-xs rounded font-medium animate-pulse">
                                <Clock className="h-3 w-3" /> En attente
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Commentaire du stagiaire */}
                        {auto.stagiaire_commentaire && (
                          <div className="mt-2 p-2 bg-white rounded border border-slate-200">
                            <p className="text-xs text-slate-500">Commentaire du stagiaire:</p>
                            <p className="text-sm text-slate-700 italic">"{auto.stagiaire_commentaire}"</p>
                          </div>
                        )}
                        
                        {/* Dates */}
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                          {auto.date_tuteur && (
                            <span>Tuteur: {new Date(auto.date_tuteur).toLocaleDateString('fr-FR')}</span>
                          )}
                          {auto.date_entreprise && (
                            <span>• Entreprise: {new Date(auto.date_entreprise).toLocaleDateString('fr-FR')}</span>
                          )}
                          {auto.stagiaire_date && (
                            <span>• Réponse: {new Date(auto.stagiaire_date).toLocaleDateString('fr-FR')}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  </div>
                )}
                
                {/* Message si pas d'autonomies */}
                {stag.autonomies.length === 0 && (
                  <p className="text-sm text-slate-500 text-center">Aucune autonomie en double validation pour ce stagiaire</p>
                )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Alertes pour les refus */}
      {data?.stagiaires?.some(s => s.stats.refusees > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-700">Autonomies refusées</h4>
              <p className="text-sm text-red-600 mt-1">
                {data.stats_global.total_refusees} autonomie(s) ont été refusée(s) par les stagiaires. 
                Un suivi peut être nécessaire pour comprendre les raisons et accompagner le stagiaire.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecapitulatifAutonomies;
